import { useState, useCallback } from "react";
import { Article } from "@/features/search/types/search";
import { supabase } from "@/api/supabase";

// e.g., src/features/trending/types/TrendingTags.ts
export interface CliffNote {
  headline: string;
  summary: string;
}

export interface TrendingTagType {
  tag: string;
  count: number;
  cliff_note?: CliffNote;
  created_at: string;
  articles?: any[]; // Add this if you need it
}

export const useTrendingTag = () => {
  const [tags, setTags] = useState<TrendingTagType[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [isFetchingTagArticles, setIsFetchingTagArticles] = useState(false);
  const [tagArticles, setTagArticles] = useState<Article[]>([]);

  const [tagsDateRange, setTagsDateRange] = useState<number>(
    () => Number(localStorage.getItem("tagsDateRange")) || 7
  );

  const handleTagsDateRangeChange = (daysAgo: number) => {
    localStorage.setItem("tagsDateRange", daysAgo.toString());
    setTagsDateRange(daysAgo);
  };

  const loadTrendingTags = useCallback(async () => {
    try {
      setIsLoadingTags(true);
      // Call your new function directly
      const { data, error } = await supabase.rpc(
        "get_latest_trending_tags_for_max_day"
      );
      if (error) {
        throw error;
      }

      // `data` is an array of objects, each with
      // { tag, article_title, article_link, cliff_note, created_at }
      console.log("Fetched latest trending tags (for max day):", data);
      console.log("max day", data[0].created_at);
      setTags(data);
    } catch (error) {
      console.error("Error in loadTrendingTags:", error);
    } finally {
      setIsLoadingTags(false);
    }
  }, []);

  const fetchArticlesByTag = async (tag: string, daysAgo: number) => {
    setIsFetchingTagArticles(true);
    try {
      const response = await fetch(
        "https://supabase-worker.marsescobin.workers.dev/api/articles-by-tag",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tag,
            daysAgo: daysAgo,
          }),
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const articles = await response.json();
      setTagArticles(articles);
      return articles;
    } catch (error) {
      console.error("Error fetching articles by tag:", error);
      setTagArticles([]);
      return []; // Return empty array on error
    } finally {
      setIsFetchingTagArticles(false);
    }
  };

  return {
    loadTrendingTags,
    tags,
    isLoadingTags,
    tagArticles,
    isFetchingTagArticles,
    fetchArticlesByTag,
    tagsDateRange,
    handleTagsDateRangeChange,
  };
};
